import React, { useMemo } from "react";

/**
 * HOFUnlockBurst — localized confetti + glow burst over a single pedestal.
 * Position-anchored by the parent (absolute left/top in %); the burst radiates
 * outward from (0,0) of this element so the parent should center it on the
 * pedestal top. Pure CSS, no deps.
 *
 * Props:
 *   color  — primary accent color (matches the pedestal/statue class).
 *   onEnd  — called ~1.8s after mount, when the animation ends.
 */
const PALETTE = ["#FFD700", "#FF007F", "#00F0FF", "#39FF14", "#9D00FF"];

export default function HOFUnlockBurst({ color = "#FFD700", onEnd }) {
  const particles = useMemo(() => {
    const n = 24;
    return Array.from({ length: n }, (_, i) => {
      const angle = (i / n) * Math.PI * 2 + Math.random() * 0.2;
      const dist = 70 + Math.random() * 60;
      return {
        id: i,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist - 20, // bias upward
        size: 5 + Math.random() * 6,
        delay: Math.random() * 0.08,
        duration: 0.9 + Math.random() * 0.6,
        rot: Math.random() * 360,
        color: Math.random() < 0.4 ? color : PALETTE[Math.floor(Math.random() * PALETTE.length)],
        shape: Math.random() < 0.55 ? "rect" : "circ",
      };
    });
  }, [color]);

  React.useEffect(() => {
    if (!onEnd) return;
    const t = setTimeout(onEnd, 1800);
    return () => clearTimeout(t);
  }, [onEnd]);

  return (
    <div
      className="pointer-events-none"
      style={{ position: "absolute", left: 0, top: 0, width: 0, height: 0, zIndex: 20 }}
      data-testid="hof-unlock-burst"
    >
      {/* Expanding glow ring */}
      <span
        className="hof-burst-ring"
        style={{
          position: "absolute",
          left: 0, top: 0,
          width: 24, height: 24,
          marginLeft: -12, marginTop: -12,
          borderRadius: "9999px",
          background: `radial-gradient(circle, ${color}CC, ${color}44 60%, transparent 72%)`,
          boxShadow: `0 0 24px ${color}, 0 0 48px ${color}88`,
        }}
      />
      {/* Starburst spokes */}
      <span
        className="hof-burst-flash"
        style={{
          position: "absolute",
          left: 0, top: 0,
          width: 8, height: 180,
          marginLeft: -4, marginTop: -90,
          background: `linear-gradient(180deg, transparent, ${color}, transparent)`,
          filter: `drop-shadow(0 0 8px ${color})`,
        }}
      />
      <span
        className="hof-burst-flash"
        style={{
          position: "absolute",
          left: 0, top: 0,
          width: 180, height: 8,
          marginLeft: -90, marginTop: -4,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          filter: `drop-shadow(0 0 8px ${color})`,
        }}
      />
      {/* Confetti particles */}
      {particles.map((p) => (
        <span
          key={p.id}
          className="hof-burst-particle"
          style={{
            position: "absolute",
            left: 0, top: 0,
            width: `${p.size}px`,
            height: p.shape === "rect" ? `${p.size * 1.6}px` : `${p.size}px`,
            marginLeft: `-${p.size / 2}px`,
            marginTop: `-${p.size / 2}px`,
            background: p.color,
            borderRadius: p.shape === "circ" ? "50%" : "2px",
            boxShadow: `0 0 8px ${p.color}`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            "--rot": `${p.rot}deg`,
          }}
        />
      ))}

      <style>{`
        @keyframes hof-burst-ring {
          0%   { transform: scale(0.4); opacity: 1; }
          60%  { opacity: 0.6; }
          100% { transform: scale(5); opacity: 0; }
        }
        .hof-burst-ring { animation: hof-burst-ring 0.9s cubic-bezier(0.22, 0.61, 0.36, 1) forwards; will-change: transform, opacity; }

        @keyframes hof-burst-flash {
          0%   { opacity: 0; transform: scale(0.4) rotate(0deg); }
          25%  { opacity: 1; }
          100% { opacity: 0; transform: scale(1.4) rotate(90deg); }
        }
        .hof-burst-flash { animation: hof-burst-flash 0.7s ease-out forwards; will-change: transform, opacity; }

        @keyframes hof-burst-particle {
          0%   { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translate3d(var(--dx), var(--dy), 0) rotate(var(--rot)); opacity: 0; }
        }
        .hof-burst-particle {
          animation-name: hof-burst-particle;
          animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
          animation-fill-mode: forwards;
          will-change: transform, opacity;
        }
      `}</style>
    </div>
  );
}
